import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetTicketsQuery as GetTickets } from '__generated__/graphql';

import { DragDropList } from '../../../../common/Common/DragDrop';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import GetTicketsQuery from './GetTicketsQuery';
import SortTicketsMutation from './SortTicketsMutation';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';

type Event = GetTickets['project']['event'];

interface TicketSorterProps {
  event: Event;
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

const TicketSorter = ({
  event, onSuccess, refetchQueries = [GetTicketsQuery],
}: TicketSorterProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      items: event.ticket_categories.map((ticketCategory) => ({
        id: ticketCategory.id,
        title: ticketCategory.title,
        children: ticketCategory.tickets.map((ticket) => ({
          id: ticket.id,
          title: ticket.title,
        })),
      })),
    },
  });

  const [sortTickets, { loading }] = useMutation(
    SortTicketsMutation,
    {
      variables: {
        input: {
          ticket_categories: form.values.items.map((ticketCategory) => ({
            id: ticketCategory.id,
            tickets: ticketCategory.children.map((ticket) => ticket.id),
          })),
        },
      },
      onCompleted: () => {
        notifier.success(t('notifications.tickets_sorted'));

        onSuccess?.();
      },
      onError: () => {
        notifier.error(t('notifications.something_went_wrong'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  return (
    <UI.Form onSubmit={() => sortTickets()}>
      <UI.GridContainer>
        <UI.SectionHeading>
          {t('sort_tickets')}
        </UI.SectionHeading>

        <UI.HR />

        <DragDropList items={form.values.items} onChange={(items) => form.set('items', items)} />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save')}
          closeTo={`/${project.id}/events/${event.id}/tickets`}
          closeLabel={t('cancel')}
          loading={loading}
        />
      </UI.GridContainer>
    </UI.Form>
  );
};

export default TicketSorter;
